import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Columns3,
  Eye,
  EyeOff,
  GripVertical,
  RotateCcw,
  Search,
  X,
} from "lucide-react";
import { PortalTableKey } from "../api/contracts";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { PortalTableColumn } from "./DataDisplay";
import { ActionTooltip } from "./ActionTooltip";

const TABLE_PREFERENCE_SCHEMA_VERSION = 1;
const SAVE_DELAY_MS = 500;

export interface PortalTableColumnRule {
  key: string;
  label?: string;
  hideable?: boolean;
  movable?: boolean;
  defaultHidden?: boolean;
  position?: "left" | "right";
}

interface PreferenceState {
  columnOrder: string[];
  hiddenColumns: string[];
  columnWidths: Record<string, number>;
}

type SaveStatus = "idle" | "loading" | "saving" | "saved" | "error";

function normalizeOrder(
  requestedOrder: readonly string[],
  defaultOrder: readonly string[],
  rules: ReadonlyMap<string, PortalTableColumnRule>,
): string[] {
  const validKeys = new Set(defaultOrder);
  const seen = new Set<string>();
  const ordered = requestedOrder.filter((key) => {
    if (!validKeys.has(key) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  defaultOrder.forEach((key, defaultIndex) => {
    if (seen.has(key)) return;

    const precedingKeys = new Set(defaultOrder.slice(0, defaultIndex));
    let insertIndex = -1;
    ordered.forEach((orderedKey, orderedIndex) => {
      if (precedingKeys.has(orderedKey)) insertIndex = orderedIndex + 1;
    });

    if (insertIndex < 0) {
      const followingKeys = new Set(defaultOrder.slice(defaultIndex + 1));
      const followingIndex = ordered.findIndex((orderedKey) =>
        followingKeys.has(orderedKey),
      );
      insertIndex = followingIndex < 0 ? ordered.length : followingIndex;
    }

    ordered.splice(insertIndex, 0, key);
    seen.add(key);
  });

  const left = defaultOrder.filter((key) => rules.get(key)?.position === "left");
  const right = defaultOrder.filter(
    (key) => rules.get(key)?.position === "right",
  );
  const positioned = new Set([...left, ...right]);
  return [...left, ...ordered.filter((key) => !positioned.has(key)), ...right];
}

function normalizePreference(
  preference: Partial<PreferenceState> | null | undefined,
  defaultOrder: readonly string[],
  rules: ReadonlyMap<string, PortalTableColumnRule>,
): PreferenceState {
  const validKeys = new Set(defaultOrder);
  const columnOrder = normalizeOrder(
    preference?.columnOrder ?? defaultOrder,
    defaultOrder,
    rules,
  );
  // Apply optional-column defaults only to keys the saved layout has not seen.
  // A saved order containing the key preserves the user's visibility choice.
  const newlyHiddenColumns = defaultOrder.filter(
    (key) => rules.get(key)?.defaultHidden && !preference?.columnOrder?.includes(key),
  );
  const hiddenColumns = [...(preference?.hiddenColumns ?? []), ...newlyHiddenColumns].filter(
    (key, index, values) =>
      validKeys.has(key) &&
      rules.get(key)?.hideable !== false &&
      values.indexOf(key) === index,
  );
  const columnWidths = Object.fromEntries(
    Object.entries(preference?.columnWidths ?? {}).filter(
      ([key, width]) =>
        validKeys.has(key) &&
        Number.isInteger(width) &&
        width >= 48 &&
        width <= 1200,
    ),
  );
  return { columnOrder, hiddenColumns, columnWidths };
}

function sameOrder(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((key, index) => key === right[index]);
}

function columnLabel<TRecord>(column: PortalTableColumn<TRecord>): string {
  if (column.headerLabel) return column.headerLabel;
  return typeof column.header === "string" ? column.header : column.key;
}

export function usePortalTablePreferences<TRecord>({
  clinicianCode,
  tableKey,
  columns,
  rules: suppliedRules = [],
}: {
  clinicianCode: string;
  tableKey: PortalTableKey;
  columns: readonly PortalTableColumn<TRecord>[];
  rules?: readonly PortalTableColumnRule[];
}) {
  const defaultOrder = useMemo(() => columns.map((column) => column.key), [columns]);
  const defaultOrderSignature = defaultOrder.join("\u001f");
  const columnRuleSignature = columns
    .map((column) =>
      [column.key, columnLabel(column), column.sticky ?? ""].join("\u001e"),
    )
    .join("\u001f");
  const ruleSignature = suppliedRules
    .map((rule) =>
      [rule.key, rule.label, rule.hideable, rule.movable, rule.defaultHidden, rule.position].join("\u001e"),
    )
    .join("\u001f");
  const rules = useMemo(() => {
    const byKey = new Map(suppliedRules.map((rule) => [rule.key, rule]));
    return new Map(
      columns.map((column) => {
        const supplied = byKey.get(column.key);
        return [
          column.key,
          {
            key: column.key,
            label: supplied?.label ?? columnLabel(column),
            hideable: supplied?.hideable ?? !column.sticky,
            movable: supplied?.movable ?? !column.sticky,
            defaultHidden: supplied?.defaultHidden ?? false,
            position: supplied?.position ?? column.sticky,
          } satisfies PortalTableColumnRule,
        ];
      }),
    );
    // Stable signatures deliberately prevent page-local render functions from
    // restarting preference loading on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnRuleSignature, ruleSignature]);
  const normalizedDefaults = useMemo(
    () => normalizePreference(null, defaultOrder, rules),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultOrderSignature, rules],
  );
  const [preference, setPreference] = useState<PreferenceState>(normalizedDefaults);
  const [status, setStatus] = useState<SaveStatus>("loading");
  const dirtyRevision = useRef(0);
  const saveController = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    dirtyRevision.current = 0;
    setPreference(normalizedDefaults);
    setStatus("loading");

    const getPreferences = clinicianPortalApi.getTablePreferences;
    if (typeof getPreferences !== "function") {
      setStatus("idle");
      return () => controller.abort();
    }

    getPreferences(clinicianCode, tableKey, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        if (dirtyRevision.current > 0) return;
        setPreference(
          normalizePreference(result.preference, defaultOrder, rules),
        );
        setStatus("idle");
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof Error && error.name === "AbortError") return;
        setStatus("error");
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicianCode, tableKey, defaultOrderSignature, rules]);

  useEffect(() => {
    if (dirtyRevision.current === 0) return;
    const revision = dirtyRevision.current;
    const timeout = window.setTimeout(() => {
      const savePreferences = clinicianPortalApi.saveTablePreferences;
      if (typeof savePreferences !== "function") {
        setStatus("saved");
        return;
      }

      saveController.current?.abort();
      const controller = new AbortController();
      saveController.current = controller;
      setStatus("saving");
      savePreferences(
        clinicianCode,
        tableKey,
        preference.columnOrder,
        preference.hiddenColumns,
        preference.columnWidths,
        TABLE_PREFERENCE_SCHEMA_VERSION,
        controller.signal,
      )
        .then(() => {
          if (!controller.signal.aborted && dirtyRevision.current === revision) {
            setStatus("saved");
          }
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) return;
          if (error instanceof Error && error.name === "AbortError") return;
          setStatus("error");
        });
    }, SAVE_DELAY_MS);

    return () => window.clearTimeout(timeout);
  }, [clinicianCode, preference, tableKey]);

  useEffect(
    () => () => {
      saveController.current?.abort();
    },
    [],
  );

  const updatePreference = useCallback(
    (update: (current: PreferenceState) => PreferenceState) => {
      dirtyRevision.current += 1;
      setPreference((current) => update(current));
      setStatus("idle");
    },
    [],
  );

  const toggleColumn = useCallback(
    (key: string) => {
      if (rules.get(key)?.hideable === false) return;
      updatePreference((current) => ({
        ...current,
        hiddenColumns: current.hiddenColumns.includes(key)
          ? current.hiddenColumns.filter((hiddenKey) => hiddenKey !== key)
          : [...current.hiddenColumns, key],
      }));
    },
    [rules, updatePreference],
  );

  const moveColumn = useCallback(
    (key: string, targetKey: string) => {
      if (key === targetKey || rules.get(key)?.movable === false) return;
      updatePreference((current) => {
        const nextOrder = current.columnOrder.filter((columnKey) => columnKey !== key);
        const targetIndex = nextOrder.indexOf(targetKey);
        nextOrder.splice(targetIndex < 0 ? nextOrder.length : targetIndex, 0, key);
        return {
          ...current,
          columnOrder: normalizeOrder(nextOrder, defaultOrder, rules),
        };
      });
    },
    [defaultOrder, rules, updatePreference],
  );

  const moveColumnBy = useCallback(
    (key: string, offset: -1 | 1) => {
      if (rules.get(key)?.movable === false) return;
      const movableOrder = preference.columnOrder.filter(
        (columnKey) => rules.get(columnKey)?.movable !== false,
      );
      const index = movableOrder.indexOf(key);
      const targetKey = movableOrder[index + offset];
      if (!targetKey) return;
      moveColumn(key, offset < 0 ? targetKey : preference.columnOrder[preference.columnOrder.indexOf(targetKey) + 1] ?? "");
    },
    [moveColumn, preference.columnOrder, rules],
  );

  const updateColumnWidths = useCallback(
    (widths: Record<string, number>) => {
      updatePreference((current) => ({
        ...current,
        columnWidths: { ...current.columnWidths, ...widths },
      }));
    },
    [updatePreference],
  );

  const reset = useCallback(
    () => updatePreference(() => normalizedDefaults),
    [normalizedDefaults, updatePreference],
  );

  const columnsByKey = new Map(columns.map((column) => [column.key, column]));
  const visibleColumns = preference.columnOrder
    .filter((key) => !preference.hiddenColumns.includes(key))
    .map((key) => columnsByKey.get(key))
    .filter((column): column is PortalTableColumn<TRecord> => Boolean(column));
  const isCustomized =
    !sameOrder(preference.columnOrder, normalizedDefaults.columnOrder) ||
    !sameOrder(
      [...preference.hiddenColumns].sort(),
      [...normalizedDefaults.hiddenColumns].sort(),
    ) ||
    Object.keys(preference.columnWidths).length > 0;

  return {
    visibleColumns,
    orderedKeys: preference.columnOrder,
    hiddenColumns: new Set(preference.hiddenColumns),
    columnWidths: preference.columnWidths,
    rules,
    status,
    isCustomized,
    toggleColumn,
    moveColumn,
    moveColumnBy,
    updateColumnWidths,
    reset,
  };
}

export function TableColumnsButton({
  preference,
  label = "Configure columns",
}: {
  preference: ReturnType<typeof usePortalTablePreferences>;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [draggedKey, setDraggedKey] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleKeys = preference.orderedKeys.filter((key) =>
    (preference.rules.get(key)?.label ?? key)
      .toLocaleLowerCase()
      .includes(normalizedQuery),
  );

  return (
    <div className="cp-table-columns" ref={containerRef} data-open={open || undefined}>
      <ActionTooltip label={label}>
        {(descriptionId) => (
          <button
            ref={triggerRef}
            type="button"
            className="cp-button cp-button--secondary cp-button--icon-only cp-table-columns__trigger"
            aria-label={label}
            aria-describedby={descriptionId}
            aria-haspopup="dialog"
            aria-expanded={open}
            data-customized={preference.isCustomized || undefined}
            onClick={() => setOpen((current) => !current)}
          >
            <Columns3 aria-hidden="true" />
          </button>
        )}
      </ActionTooltip>
      {open ? (
        <div className="cp-table-columns__panel" role="dialog" aria-label="Configure columns">
          <div className="cp-table-columns__header">
            <div>
              <strong>Columns</strong>
              <span>Show, hide, or reorder fields</span>
            </div>
            <button type="button" aria-label="Close column settings" onClick={() => setOpen(false)}>
              <X aria-hidden="true" />
            </button>
          </div>
          <label className="cp-table-columns__search">
            <Search aria-hidden="true" />
            <span className="cp-visually-hidden">Find a column</span>
            <input
              type="search"
              value={query}
              placeholder="Find a column"
              onChange={(event) => setQuery(event.currentTarget.value)}
            />
          </label>
          <div className="cp-table-columns__list">
            {visibleKeys.map((key) => {
              const rule = preference.rules.get(key);
              const hidden = preference.hiddenColumns.has(key);
              const movable = rule?.movable !== false;
              return (
                <div
                  className="cp-table-columns__row"
                  data-dragging={draggedKey === key || undefined}
                  key={key}
                  onDragOver={(event) => {
                    if (draggedKey && draggedKey !== key) event.preventDefault();
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (draggedKey) preference.moveColumn(draggedKey, key);
                    setDraggedKey(null);
                  }}
                >
                  <button
                    type="button"
                    className="cp-table-columns__drag"
                    draggable={movable}
                    disabled={!movable}
                    aria-label={`Move ${rule?.label ?? key} column`}
                    title={movable ? "Drag to reorder; use arrow keys to move" : "This column has a fixed position"}
                    onDragStart={(event) => {
                      setDraggedKey(key);
                      event.dataTransfer.effectAllowed = "move";
                    }}
                    onDragEnd={() => setDraggedKey(null)}
                    onKeyDown={(event) => {
                      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
                      event.preventDefault();
                      preference.moveColumnBy(key, event.key === "ArrowUp" ? -1 : 1);
                    }}
                  >
                    <GripVertical aria-hidden="true" />
                  </button>
                  <span className="cp-table-columns__label">
                    {rule?.label ?? key}
                  </span>
                  <button
                    type="button"
                    className="cp-table-columns__visibility"
                    aria-label={`${hidden ? "Show" : "Hide"} ${rule?.label ?? key} column`}
                    aria-pressed={!hidden}
                    disabled={rule?.hideable === false}
                    title={
                      rule?.hideable === false
                        ? "This column is always visible"
                        : hidden
                          ? "Show column"
                          : "Hide column"
                    }
                    onClick={() => preference.toggleColumn(key)}
                  >
                    {hidden ? (
                      <EyeOff aria-hidden="true" />
                    ) : (
                      <Eye aria-hidden="true" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
          <div className="cp-table-columns__footer">
            <button type="button" onClick={preference.reset} disabled={!preference.isCustomized}>
              <RotateCcw aria-hidden="true" />
              Reset defaults
            </button>
            <span role="status">
              {preference.status === "saving"
                ? "Saving…"
                : preference.status === "saved"
                  ? "Saved"
                  : preference.status === "error"
                    ? "Could not save"
                    : ""}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
